import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import moment from "moment";
import { getRequest, postRequest } from "../../utils/api";
import { logout } from "../../utils/auth";
import Pill from "../../Component/Pill";
import AffiliateLayout from "./components/AffiliateLayout";

const EMPTY_FORM = {
    _id: "",
    commission_id: "",
    commission_title: "",
    commission_amount: "",
    commission_messaging_amount: "",
    commission_min_users: "",
    commission_publish_type: 0,
};

const CommissionTier = () => {
    const [commissionList, setCommissionList] = useState<any[]>([]);
    const [isLoader, setIsLoader] = useState<boolean>(true);
    const [isSaving, setIsSaving] = useState<boolean>(false);

    // Tier form (empty _id = new tier)
    const [formData, setFormData] = useState(EMPTY_FORM);
    const isEdit = !!formData._id;

    // Fetch Commission Tiers
    const getCommissionTierList = async () => {
        try {
            setIsLoader(true);
            const response = await getRequest(`/affiliate/commission-list`);

            if (response.status === "success") {
                setCommissionList(response.data);
            } else if (response.status === "session_expired") {
                logout();
            } else {
                toast.error(response.message);
            }
        } catch (error) {
            toast.error("Something went wrong loading commission tiers!");
        } finally {
            setIsLoader(false);
        }
    };

    useEffect(() => {
        getCommissionTierList();
    }, []);

    const handleSave = async () => {
        const amount = Number(formData.commission_amount);
        const messagingAmount = Number(formData.commission_messaging_amount || 0);
        if (!formData.commission_title.trim()) return toast.warning("Tier title is required");
        if (formData.commission_amount === "" || isNaN(amount) || amount <= 0 || amount > 100) {
            return toast.warning("Plan commission % must be between 0 and 100");
        }
        if (isNaN(messagingAmount) || messagingAmount < 0 || messagingAmount > 100) {
            return toast.warning("Messaging commission % must be between 0 and 100");
        }
        const minUsers = Number(formData.commission_min_users || 0);
        if (!Number.isInteger(minUsers) || minUsers < 0) {
            return toast.warning("Minimum users must be a whole number (0 or more)");
        }

        try {
            setIsSaving(true);
            const response = await postRequest("/affiliate/add-commission-tier", {
                _id: formData._id,
                commission_id: formData.commission_id,
                commission_title: formData.commission_title.trim(),
                commission_amount: formData.commission_amount,
                // blank = 0% on messaging
                commission_messaging_amount: formData.commission_messaging_amount === "" ? "0" : formData.commission_messaging_amount,
                // blank = no minimum
                commission_min_users: minUsers,
                commission_publish_type: formData.commission_publish_type,
            });

            if (response.status === "success") {
                toast.success(response.message);
                setFormData(EMPTY_FORM);
                getCommissionTierList();
            } else if (response.status === "session_expired") {
                logout();
            } else {
                toast.error(response.message);
            }
        } catch (error: any) {
            toast.error(typeof error === "string" ? error : "Something went wrong");
        } finally {
            setIsSaving(false);
        }
    };

    const editTier = (item: any) => {
        setFormData({
            _id: item._id,
            commission_id: item.commission_id,
            commission_title: item.commission_title,
            commission_amount: item.commission_amount,
            commission_messaging_amount: item.commission_messaging_amount ?? "0",
            commission_min_users: String(item.commission_min_users ?? 0),
            commission_publish_type: Number(item.commission_publish_type) === 1 ? 1 : 0,
        });
    };

    const deleteTier = async (item: any) => {
        if (!window.confirm(`Delete the "${item.commission_title}" tier permanently? This cannot be undone.`)) return;

        try {
            setIsLoader(true);
            const response = await postRequest("/affiliate/deleteCommissionTier", { _id: item._id });

            if (response.status === "success") {
                toast.success(response.message);
                if (formData._id === item._id) setFormData(EMPTY_FORM);
                getCommissionTierList();
            } else if (response.status === "session_expired") {
                logout();
            } else {
                toast.error(response.message);
            }
        } catch (error: any) {
            toast.error(typeof error === "string" ? error : "Something went wrong");
        } finally {
            setIsLoader(false);
        }
    };

    return (
        <AffiliateLayout
            loading={isLoader}
            actions={
                <button type="button" className="cx-btn cx-btn-primary" onClick={() => setFormData(EMPTY_FORM)}>
                    <i className="ri-add-line"></i>New tier
                </button>
            }
        >
            <div className="cx-grid g12">
                <div className="cx-card" style={{ alignSelf: "start" }}>
                    <div className="cx-card-h">
                        <h6>{isEdit ? `Edit · ${formData.commission_title || "tier"}` : "New commission tier"}</h6>
                        {isEdit && <Pill tone="blue">Editing</Pill>}
                    </div>
                    <div className="cx-card-b" style={{ padding: 16 }}>
                        <div className="cx-fgrid">
                            <div className="full">
                                <label className="cx-label">Tier title</label>
                                <input
                                    className="cx-input"
                                    placeholder="e.g. Gold partner"
                                    value={formData.commission_title}
                                    onChange={(e) => setFormData(prev => ({ ...prev, commission_title: e.target.value }))}
                                />
                            </div>
                            <div>
                                <label className="cx-label">Plan commission %</label>
                                <input
                                    className="cx-input mono"
                                    type="number"
                                    min="0"
                                    max="100"
                                    step="0.5"
                                    placeholder="e.g. 20"
                                    value={formData.commission_amount}
                                    onChange={(e) => setFormData(prev => ({ ...prev, commission_amount: e.target.value }))}
                                />
                                <p className="cx-help">Of each plan invoice (before tax)</p>
                            </div>
                            <div>
                                <label className="cx-label">Messaging commission %</label>
                                <input
                                    className="cx-input mono"
                                    type="number"
                                    min="0"
                                    max="100"
                                    step="0.5"
                                    placeholder="e.g. 5"
                                    value={formData.commission_messaging_amount}
                                    onChange={(e) => setFormData(prev => ({ ...prev, commission_messaging_amount: e.target.value }))}
                                />
                                <p className="cx-help">Of our messaging profit on credit-line clients (billed − Meta cost), monthly · blank = 0</p>
                            </div>
                            <div className="full">
                                <label className="cx-label">Minimum users for this tier</label>
                                <input
                                    className="cx-input mono"
                                    type="number"
                                    min="0"
                                    step="1"
                                    placeholder="e.g. 25"
                                    value={formData.commission_min_users}
                                    onChange={(e) => setFormData(prev => ({ ...prev, commission_min_users: e.target.value }))}
                                />
                                <p className="cx-help">Referred users an affiliate needs before moving up to this tier · blank = no minimum. Saved for now — tiers are not upgraded automatically yet.</p>
                            </div>
                            <div className="full">
                                <div className="cx-orow" style={{ alignItems: "center", paddingTop: 4 }}>
                                    <div className={`cx-ic ${formData.commission_publish_type === 1 ? "ok" : "blue"}`}><i className="ri-star-line"></i></div>
                                    <div>
                                        <div className="t">Default tier</div>
                                        <div className="d">Given to every new affiliate. Only one tier can be the default.</div>
                                    </div>
                                    <div className="right">
                                        <label className="cx-switch">
                                            <input
                                                type="checkbox"
                                                checked={formData.commission_publish_type === 1}
                                                onChange={(e) => setFormData(prev => ({ ...prev, commission_publish_type: e.target.checked ? 1 : 0 }))}
                                            />
                                            <span></span>
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <p className="cx-help">A new rate applies to commissions earned from now on — past commissions keep the rate they were earned at.</p>
                    </div>
                    <div className="cx-dr-foot">
                        {isEdit && <button type="button" className="cx-btn cx-btn-light" onClick={() => setFormData(EMPTY_FORM)}>Cancel</button>}
                        <button type="button" className="cx-btn cx-btn-primary" onClick={handleSave} disabled={isSaving}>
                            {isSaving ? "Saving…" : isEdit ? "Save changes" : "Add tier"}
                        </button>
                    </div>
                </div>

                <div className="cx-card">
                    <div className="cx-tbl-wrap">
                        <table className="cx-tbl">
                            <thead>
                                <tr>
                                    <th>Tier</th>
                                    <th className="c">Plans</th>
                                    <th className="c">Messaging</th>
                                    <th className="c">Min. users</th>
                                    <th>Default</th>
                                    <th>Created</th>
                                    <th className="r">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {commissionList.length > 0 ? (
                                    commissionList.map((item) => {
                                        const isDefault = Number(item.commission_publish_type) === 1;
                                        return (
                                            <tr key={item._id} className="click" onClick={() => editTier(item)} style={formData._id === item._id ? { background: "#f2f6fc" } : undefined}>
                                                <td>
                                                    <b style={{ fontWeight: 600 }}>{item.commission_title}</b>
                                                    <div className="fs12 muted mono">#{item.commission_id}</div>
                                                </td>
                                                <td className="c mono" style={{ fontWeight: 600 }}>{item.commission_amount}%</td>
                                                <td className={`c mono ${Number(item.commission_messaging_amount) > 0 ? "" : "muted"}`} style={{ fontWeight: 600 }}>
                                                    {Number(item.commission_messaging_amount || 0)}%
                                                </td>
                                                <td className={`c mono ${Number(item.commission_min_users) > 0 ? "" : "muted"}`}>
                                                    {Number(item.commission_min_users) > 0 ? Number(item.commission_min_users).toLocaleString("en-IN") : "—"}
                                                </td>
                                                <td>{isDefault ? <Pill tone="ok">Default</Pill> : <span className="muted">—</span>}</td>
                                                <td className="fs12 muted">{moment(item.createdAt).format("DD MMM YYYY")}</td>
                                                <td onClick={(e) => e.stopPropagation()}>
                                                    <div className="cx-ra">
                                                        <button type="button" className="v" title="Edit" onClick={() => editTier(item)}>
                                                            <i className="ri-pencil-line"></i>
                                                        </button>
                                                        <button
                                                            type="button"
                                                            title={isDefault ? "The default tier can't be deleted" : "Delete"}
                                                            disabled={isDefault}
                                                            style={isDefault ? { opacity: .4, cursor: "default" } : undefined}
                                                            onClick={() => deleteTier(item)}
                                                        >
                                                            <i className="ri-delete-bin-line"></i>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan={7}>
                                            <div className="cx-empty">
                                                <i className="ri-percent-line"></i>
                                                {isLoader ? "Loading…" : "No commission tiers yet — add the first one on the left."}
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </AffiliateLayout>
    );
};

export default CommissionTier;
